import type { ReactElement } from "react";

export type CaseReviewIconName =
  | "chevronLeft" | "chevronRight" | "chevronDown" | "check" | "x" | "info" | "filter" | "user" | "clipboard"
  | "menu" | "panel" | "file" | "arrow" | "back" | "more" | "search" | "spark" | "undo" | "redo" | "expand" | "box" | "calendar" | "plus" | "trash"
  | "warning" | "pencil";

/* Path data follows the design reference so the glyphs match it exactly. */
const GLYPHS: Record<CaseReviewIconName, ReactElement> = {
  chevronLeft: <path d="M15 6l-6 6 6 6" />,
  chevronRight: <path d="M9 6l6 6-6 6" />,
  chevronDown: <path d="M6 9l6 6 6-6" />,
  check: <path d="M5 12l4.5 4.5L19 7" />,
  x: <path d="M6 6l12 12M18 6L6 18" />,
  // A failed advisory checkpoint: not a refusal, so neither the tick nor the cross.
  warning: <><path d="M12 3.8 2.7 19.2a1 1 0 0 0 .86 1.5h16.88a1 1 0 0 0 .86-1.5z" /><path d="M12 9.8v4.6" /><circle cx="12" cy="17.4" r="1.1" fill="currentColor" stroke="none" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v6" /><circle cx="12" cy="7.5" r="1.1" fill="currentColor" stroke="none" /></>,
  filter: <path d="M3 5h18l-7 8v6l-4 2v-8z" />,
  user: <><circle cx="12" cy="8" r="3.5" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></>,
  clipboard: <><rect x="8" y="2.5" width="8" height="4" rx="1" /><path d="M16 4.5h2a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2h2" /></>,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  panel: <><rect x="3" y="4" width="18" height="16" rx="2" /><line x1="14" y1="4" x2="14" y2="20" /></>,
  file: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></>,
  arrow: <path d="M4 12h15M13 6l6 6-6 6" />,
  back: <><path d="M19 12H5" /><path d="M11 18l-6-6 6-6" /></>,
  more: <><circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="1.6" fill="currentColor" stroke="none" /></>,
  search: <><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" /></>,
  spark: <><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /><path d="M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z" /></>,
  undo: <><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></>,
  redo: <><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 4v5h-5" /></>,
  expand: <><path d="M15 4h5v5" /><path d="M9 20H4v-5" /><path d="M20 4l-6 6" /><path d="M4 20l6-6" /></>,
  box: <><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" /><path d="M4 7.5l8 4.5 8-4.5M12 12v9" /></>,
  calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  pencil: <><path d="M4 20h4L18.5 9.5a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4 16z" /><path d="M13.5 6.5l4 4" /></>,
  trash: <><path d="M4 7h16" /><path d="M10 11v6M14 11v6" /><path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" /><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>,
};

interface Props { name: CaseReviewIconName; size?: number; strokeWidth?: number; className?: string }

/** Line icon set of the case review; sizes and stroke widths are passed per use, as the reference does. */
export function CaseReviewIcon({ name, size = 15, strokeWidth = 1.8, className }: Props) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className={className ? `cr-icon ${className}` : "cr-icon"}>{GLYPHS[name]}</svg>;
}
